import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { 
  Shield, Users, TrendingUp, Building2, CheckCircle, AlertTriangle, 
  Globe, BarChart3, Target, Search, Leaf, Lock, 
  ChevronRight, Layers, Briefcase, Zap, Eye,
  Rocket, X, FileText, Download, ClipboardCheck, ArrowRight
} from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import { useToast } from "@/hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Governance Framework
const GOVERNANCE_PILLARS = [
  { id: "compliance", name: "Compliance", icon: Lock, color: "#3b82f6", description: "Regulatory & legal readiness" },
  { id: "operations", name: "Operations", icon: Layers, color: "#8b5cf6", description: "Process efficiency & clarity" },
  { id: "growth", name: "Growth", icon: TrendingUp, color: "#10b981", description: "Scalability & expansion" },
  { id: "sustainability", name: "ESG", icon: Leaf, color: "#22c55e", description: "Environmental & social governance" },
];

const GovernanceDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [startups, setStartups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [portfolioScore, setPortfolioScore] = useState(72);
  
  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentStep, setAssessmentStep] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [showAssessmentResults, setShowAssessmentResults] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/startups`);
      const enrichedStartups = (res.data || []).map(s => ({
        ...s,
        governanceScore: Math.floor(Math.random() * 40) + 55,
        scores: {
          compliance: Math.floor(Math.random() * 30) + 60,
          operations: Math.floor(Math.random() * 30) + 55,
          growth: Math.floor(Math.random() * 30) + 65,
          sustainability: Math.floor(Math.random() * 30) + 50,
        },
        trend: Math.random() > 0.5 ? "up" : "stable",
        riskLevel: Math.random() > 0.7 ? "low" : Math.random() > 0.4 ? "medium" : "high"
      }));
      setStartups(enrichedStartups);
      if (enrichedStartups.length > 0) {
        setSelectedStartup(enrichedStartups[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const filteredStartups = startups.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.industry?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#C8A84B]";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-[#C8A84B]/80/10 border-emerald-500/30";
    if (score >= 60) return "bg-[#C8A84B]/10 border-yellow-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  const getRiskBadge = (risk) => {
    const styles = {
      low: "bg-[#C8A84B]/80/20 text-emerald-400 border-emerald-500/30",
      medium: "bg-[#C8A84B]/20 text-yellow-400 border-yellow-500/30",
      high: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return styles[risk] || styles.medium;
  };

  const radarData = selectedStartup ? GOVERNANCE_PILLARS.map(p => ({
    pillar: p.name,
    score: selectedStartup.scores[p.id],
    fullMark: 100
  })) : [];

  // Assessment Questions
  const assessmentQuestions = [
    {
      category: "Compliance",
      question: "Does your startup have documented compliance policies?",
      options: ["Not yet", "In progress", "Partially documented", "Fully documented"]
    },
    {
      category: "Operations",
      question: "How would you rate your operational processes?",
      options: ["Ad-hoc", "Basic structure", "Well-defined", "Optimized & automated"]
    },
    {
      category: "Growth",
      question: "What is your current growth trajectory?",
      options: ["Pre-revenue", "Early traction", "Scaling", "Rapid expansion"]
    },
    {
      category: "ESG",
      question: "Do you have ESG initiatives in place?",
      options: ["Not considered", "Planning phase", "Some initiatives", "Comprehensive program"]
    }
  ];

  const handleAssessmentAnswer = (questionIndex, answerIndex) => {
    setAssessmentAnswers({ ...assessmentAnswers, [questionIndex]: answerIndex });
    if (questionIndex < assessmentQuestions.length - 1) {
      setAssessmentStep(questionIndex + 1);
    }
  };

  const submitAssessment = () => {
    // Calculate scores based on answers (0-3 maps to 25-100)
    const categoryScores = {
      compliance: ((assessmentAnswers[0] || 0) + 1) * 25,
      operations: ((assessmentAnswers[1] || 0) + 1) * 25,
      growth: ((assessmentAnswers[2] || 0) + 1) * 25,
      sustainability: ((assessmentAnswers[3] || 0) + 1) * 25,
    };
    
    const overallScore = Math.round(
      (categoryScores.compliance + categoryScores.operations + categoryScores.growth + categoryScores.sustainability) / 4
    );
    
    // Generate recommendations based on scores
    const recommendations = [];
    if (categoryScores.compliance < 75) {
      recommendations.push({ category: "Compliance", priority: "high", text: "Prioritize creating comprehensive compliance documentation and policies" });
    }
    if (categoryScores.operations < 75) {
      recommendations.push({ category: "Operations", priority: "medium", text: "Implement structured operational processes and KPI tracking" });
    }
    if (categoryScores.growth < 75) {
      recommendations.push({ category: "Growth", priority: "medium", text: "Focus on scaling strategies and market expansion plans" });
    }
    if (categoryScores.sustainability < 75) {
      recommendations.push({ category: "ESG", priority: "low", text: "Develop ESG initiatives to improve investor appeal" });
    }
    
    if (recommendations.length === 0) {
      recommendations.push({ category: "All", priority: "low", text: "Maintain your excellent governance standards and continue monitoring" });
    }
    
    // Determine grade
    let grade = "D";
    let gradeColor = "text-red-500";
    if (overallScore >= 90) { grade = "A+"; gradeColor = "text-emerald-400"; }
    else if (overallScore >= 80) { grade = "A"; gradeColor = "text-[#C8A84B]"; }
    else if (overallScore >= 70) { grade = "B"; gradeColor = "text-blue-400"; }
    else if (overallScore >= 60) { grade = "C"; gradeColor = "text-yellow-400"; }
    else if (overallScore >= 50) { grade = "D"; gradeColor = "text-orange-400"; }
    else { grade = "F"; gradeColor = "text-red-500"; }
    
    setAssessmentResults({
      overallScore,
      grade,
      gradeColor,
      categoryScores,
      recommendations,
      answers: assessmentAnswers,
      completedAt: new Date().toLocaleString()
    });
    
    setShowAssessmentResults(true);
  };

  const downloadReport = () => {
    toast({
      title: "Report Downloaded",
      description: `Governance report for ${selectedStartup?.name} has been downloaded.`,
    });
    setShowReportModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-[rgba(13,27,42,0.08)]">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-emerald-600/10" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C8A84B]/80/20 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 py-12 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg shadow-blue-500/25">
                  <Shield className="h-8 w-8 text-[#0D1B2A]" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-[#0D1B2A] tracking-tight">Governance Hub</h1>
                  <p className="text-[#3A4A5C]">Real-time portfolio governance & risk intelligence</p>
                </div>
              </div>
            </div>
            
            {/* Portfolio Score Card */}
            <Card className="bg-white/5 backdrop-blur-xl shadow-sm border-[rgba(13,27,42,0.1)] w-72">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-[#3A4A5C]">Portfolio Health</span>
                  <Badge className="bg-[#C8A84B]/80/20 text-emerald-400 border-emerald-500/30">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    +4.2%
                  </Badge>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-5xl font-bold text-[#0D1B2A]">{portfolioScore}</span>
                  <span className="text-[#7A8190] mb-2">/100</span>
                </div>
                <Progress value={portfolioScore} className="h-2 mt-3" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          
          {/* Left Panel - Startup List */}
          <div className="lg:col-span-4 space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#7A8190]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search startups..."
                className="pl-12 bg-white/5 border-[rgba(13,27,42,0.1)] text-[#0D1B2A] placeholder:text-[#7A8190] h-12 rounded-xl"
                data-testid="governance-search"
              />
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Total", value: startups.length, icon: Building2, color: "blue" },
                { label: "At Risk", value: startups.filter(s => s.riskLevel === "high").length, icon: AlertTriangle, color: "red" },
                { label: "Strong", value: startups.filter(s => s.governanceScore >= 75).length, icon: CheckCircle, color: "green" },
              ].map((stat, i) => (
                <Card key={i} className="bg-white/5 border-[rgba(13,27,42,0.1)]">
                  <CardContent className="p-4 text-center">
                    <stat.icon className={`h-5 w-5 mx-auto mb-2 text-${stat.color}-400`} />
                    <div className="text-2xl font-bold text-[#0D1B2A]">{stat.value}</div>
                    <div className="text-xs text-[#7A8190]">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Startup List */}
            <Card className="bg-white/5 border-[rgba(13,27,42,0.1)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-[#0D1B2A] text-lg flex items-center justify-between">
                  <span>Portfolio Companies</span>
                  <Badge variant="outline" className="text-[#3A4A5C] border-[rgba(13,27,42,0.1)]">
                    {filteredStartups.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center text-[#7A8190]">Loading...</div>
                  ) : filteredStartups.length === 0 ? (
                    <div className="p-8 text-center text-[#7A8190]">No startups found</div>
                  ) : (
                    filteredStartups.map((startup) => (
                      <div
                        key={startup.id}
                        onClick={() => setSelectedStartup(startup)}
                        className={`p-4 border-b border-[rgba(13,27,42,0.08)] cursor-pointer transition-all hover:bg-white/5 ${
                          selectedStartup?.id === startup.id ? "bg-white/10 border-l-2 border-l-emerald-500" : ""
                        }`}
                        data-testid={`startup-${startup.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-[#0D1B2A] font-bold ${
                              startup.governanceScore >= 75 ? "bg-[#C8A84B]/80/20" : 
                              startup.governanceScore >= 60 ? "bg-[#C8A84B]/20" : "bg-red-500/20"
                            }`}>
                              {startup.name?.charAt(0) || "S"}
                            </div>
                            <div>
                              <h3 className="font-semibold text-[#0D1B2A]">{startup.name || "Unnamed"}</h3>
                              <p className="text-sm text-[#7A8190]">{startup.industry || "Technology"}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${getScoreColor(startup.governanceScore)}`}>
                              {startup.governanceScore}
                            </div>
                            <Badge className={getRiskBadge(startup.riskLevel)}>
                              {startup.riskLevel}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Detail View */}
          <div className="lg:col-span-8 space-y-6">
            {selectedStartup ? (
              <>
                {/* Company Header */}
                <Card className="bg-gradient-to-br from-white/10 to-white/5 border-[rgba(13,27,42,0.1)] overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-6">
                        <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold text-[#0D1B2A] ${getScoreBg(selectedStartup.governanceScore)}`}>
                          {selectedStartup.name?.charAt(0) || "S"}
                        </div>
                        <div>
                          <h2 className="text-3xl font-bold text-[#0D1B2A] mb-2">{selectedStartup.name}</h2>
                          <div className="flex items-center gap-4 text-[#3A4A5C]">
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-4 w-4" />
                              {selectedStartup.industry || "Technology"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Globe className="h-4 w-4" />
                              {selectedStartup.location || "Global"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {selectedStartup.team_size || "10-50"} employees
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Score Circle */}
                      <div className="text-center">
                        <div className={`w-28 h-28 rounded-full border-4 ${
                          selectedStartup.governanceScore >= 75 ? "border-emerald-500" :
                          selectedStartup.governanceScore >= 60 ? "border-yellow-500" : "border-red-500"
                        } flex items-center justify-center bg-black/30`}>
                          <div>
                            <div className={`text-4xl font-bold ${getScoreColor(selectedStartup.governanceScore)}`}>
                              {selectedStartup.governanceScore}
                            </div>
                            <div className="text-xs text-[#7A8190]">SCORE</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Governance Pillars */}
                <div className="grid md:grid-cols-4 gap-4">
                  {GOVERNANCE_PILLARS.map((pillar) => {
                    const score = selectedStartup.scores[pillar.id];
                    return (
                      <Card key={pillar.id} className="bg-white/5 border-[rgba(13,27,42,0.1)] hover:bg-white/10 transition-all cursor-pointer group">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-2 rounded-xl" style={{ backgroundColor: `${pillar.color}20` }}>
                              <pillar.icon className="h-5 w-5" style={{ color: pillar.color }} />
                            </div>
                            <ChevronRight className="h-4 w-4 text-[#3A4A5C] group-hover:text-white transition-colors" />
                          </div>
                          <h3 className="font-semibold text-[#0D1B2A] mb-1">{pillar.name}</h3>
                          <p className="text-xs text-[#7A8190] mb-4">{pillar.description}</p>
                          <div className="flex items-end justify-between">
                            <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{score}</span>
                            <Progress value={score} className="w-20 h-1.5" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Radar Chart & Recommendations */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Radar Chart */}
                  <Card className="bg-white/5 border-[rgba(13,27,42,0.1)]">
                    <CardHeader>
                      <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-400" />
                        Governance Profile
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={radarData}>
                            <PolarGrid stroke="#374151" />
                            <PolarAngleAxis dataKey="pillar" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                            <Radar
                              name="Score"
                              dataKey="score"
                              stroke="#10b981"
                              fill="#10b981"
                              fillOpacity={0.3}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  <Card className="bg-white/5 border-[rgba(13,27,42,0.1)]">
                    <CardHeader>
                      <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                        <Target className="h-5 w-5 text-purple-400" />
                        AI Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {[
                          { priority: "high", text: "Strengthen compliance documentation for EU markets", icon: Lock },
                          { priority: "medium", text: "Implement automated KPI tracking dashboard", icon: BarChart3 },
                          { priority: "low", text: "Expand ESG reporting for investor readiness", icon: Leaf },
                        ].map((rec, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-black/20 border border-[rgba(13,27,42,0.08)]">
                            <div className={`p-2 rounded-lg ${
                              rec.priority === "high" ? "bg-red-500/20" :
                              rec.priority === "medium" ? "bg-[#C8A84B]/20" : "bg-[#C8A84B]/12/20"
                            }`}>
                              <rec.icon className={`h-4 w-4 ${
                                rec.priority === "high" ? "text-red-400" :
                                rec.priority === "medium" ? "text-yellow-400" : "text-blue-400"
                              }`} />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-[#3A4A5C]">{rec.text}</p>
                              <Badge variant="outline" className={`mt-2 text-xs ${
                                rec.priority === "high" ? "border-red-500/50 text-red-400" :
                                rec.priority === "medium" ? "border-yellow-500/50 text-yellow-400" : "border-blue-500/50 text-blue-400"
                              }`}>
                                {rec.priority} priority
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <Card className="bg-gradient-to-r from-emerald-900/30 to-blue-900/30 border-emerald-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Zap className="h-8 w-8 text-emerald-400" />
                        <div>
                          <h3 className="text-lg font-semibold text-[#0D1B2A]">Ready to improve governance?</h3>
                          <p className="text-[#3A4A5C]">Get personalized recommendations and action plans</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          className="border-white/20 text-[#0D1B2A] hover:bg-white/10"
                          onClick={() => setShowReportModal(true)}
                          data-testid="view-report-btn"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Full Report
                        </Button>
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setShowAssessmentModal(true)}
                          data-testid="start-assessment-btn"
                        >
                          <Rocket className="h-4 w-4 mr-2" />
                          Start Assessment
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-white/5 border-[rgba(13,27,42,0.1)]">
                <CardContent className="p-20 text-center">
                  <Shield className="h-16 w-16 mx-auto mb-4 text-[#3A4A5C]" />
                  <h3 className="text-xl font-semibold text-[#3A4A5C] mb-2">Select a Startup</h3>
                  <p className="text-[#7A8190]">Choose a company from the list to view governance details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Full Report Modal */}
      {showReportModal && selectedStartup && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-[rgba(13,27,42,0.1)] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[rgba(13,27,42,0.1)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-blue-400" />
                <h2 className="text-2xl font-bold text-[#0D1B2A]">Governance Report</h2>
              </div>
              <Button variant="ghost" onClick={() => setShowReportModal(false)} className="text-[#3A4A5C] hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Company Header */}
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold text-[#0D1B2A] ${getScoreBg(selectedStartup.governanceScore)}`}>
                  {selectedStartup.name?.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0D1B2A]">{selectedStartup.name}</h3>
                  <p className="text-[#3A4A5C]">{selectedStartup.industry} • {selectedStartup.location}</p>
                </div>
                <div className="ml-auto text-center">
                  <div className={`text-4xl font-bold ${getScoreColor(selectedStartup.governanceScore)}`}>
                    {selectedStartup.governanceScore}
                  </div>
                  <div className="text-xs text-[#7A8190]">Overall Score</div>
                </div>
              </div>

              {/* Pillar Scores */}
              <div>
                <h4 className="text-lg font-semibold text-[#0D1B2A] mb-4">Governance Pillars</h4>
                <div className="space-y-3">
                  {GOVERNANCE_PILLARS.map(pillar => (
                    <div key={pillar.id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                      <pillar.icon className="h-5 w-5" style={{ color: pillar.color }} />
                      <span className="text-[#0D1B2A] flex-1">{pillar.name}</span>
                      <Progress value={selectedStartup.scores[pillar.id]} className="w-32 h-2" />
                      <span className={`font-bold w-12 text-right ${getScoreColor(selectedStartup.scores[pillar.id])}`}>
                        {selectedStartup.scores[pillar.id]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Findings */}
              <div>
                <h4 className="text-lg font-semibold text-[#0D1B2A] mb-4">Key Findings</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 bg-[#C8A84B]/80/10 border border-emerald-500/30 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5" />
                    <p className="text-[#3A4A5C]">Strong growth trajectory with consistent month-over-month improvements</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-[#C8A84B]/10 border border-yellow-500/30 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
                    <p className="text-[#3A4A5C]">Compliance documentation needs improvement before Series A</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <Target className="h-5 w-5 text-blue-400 mt-0.5" />
                    <p className="text-[#3A4A5C]">Consider expanding ESG initiatives for better investor appeal</p>
                  </div>
                </div>
              </div>

              {/* Action Items */}
              <div>
                <h4 className="text-lg font-semibold text-[#0D1B2A] mb-4">Recommended Actions</h4>
                <div className="space-y-2">
                  {[
                    "Complete GDPR compliance documentation",
                    "Implement board meeting cadence (monthly)",
                    "Set up financial controls dashboard",
                    "Create ESG policy document"
                  ].map((action, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-[#C8A84B]/80/20 flex items-center justify-center text-emerald-400 text-sm font-bold">
                        {i + 1}
                      </div>
                      <span className="text-[#3A4A5C]">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[rgba(13,27,42,0.1)] flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowReportModal(false)} className="border-white/20 text-[#0D1B2A]">
                Close
              </Button>
              <Button onClick={downloadReport} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-[rgba(13,27,42,0.1)] rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-[rgba(13,27,42,0.1)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ClipboardCheck className="h-6 w-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-[#0D1B2A]">Governance Assessment</h2>
              </div>
              <Button variant="ghost" onClick={() => { setShowAssessmentModal(false); setAssessmentStep(0); }} className="text-[#3A4A5C] hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-[#3A4A5C] mb-2">
                  <span>Question {assessmentStep + 1} of {assessmentQuestions.length}</span>
                  <span>{Math.round(((assessmentStep + 1) / assessmentQuestions.length) * 100)}% Complete</span>
                </div>
                <Progress value={((assessmentStep + 1) / assessmentQuestions.length) * 100} className="h-2" />
              </div>

              {/* Question */}
              <div className="mb-8">
                <Badge className="bg-purple-500/20 text-purple-400 mb-4">
                  {assessmentQuestions[assessmentStep].category}
                </Badge>
                <h3 className="text-xl font-semibold text-[#0D1B2A] mb-6">
                  {assessmentQuestions[assessmentStep].question}
                </h3>
                
                <div className="space-y-3">
                  {assessmentQuestions[assessmentStep].options.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => handleAssessmentAnswer(assessmentStep, i)}
                      className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between group ${
                        assessmentAnswers[assessmentStep] === i 
                          ? "bg-[#C8A84B]/80/20 border-emerald-500 text-[#0D1B2A]" 
                          : "bg-white/5 border-[rgba(13,27,42,0.1)] text-[#3A4A5C] hover:bg-white/10 hover:border-white/30"
                      }`}
                    >
                      <span>{option}</span>
                      <ArrowRight className={`h-5 w-5 transition-transform ${
                        assessmentAnswers[assessmentStep] === i ? "text-emerald-400" : "text-[#3A4A5C] group-hover:translate-x-1"
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[rgba(13,27,42,0.1)] flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => setAssessmentStep(Math.max(0, assessmentStep - 1))}
                disabled={assessmentStep === 0}
                className="border-white/20 text-[#0D1B2A] disabled:opacity-50"
              >
                Previous
              </Button>
              
              {assessmentStep === assessmentQuestions.length - 1 ? (
                <Button 
                  onClick={submitAssessment}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={assessmentAnswers[assessmentStep] === undefined}
                >
                  Submit Assessment
                </Button>
              ) : (
                <Button 
                  onClick={() => setAssessmentStep(assessmentStep + 1)}
                  disabled={assessmentAnswers[assessmentStep] === undefined}
                  className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold"
                >
                  Next Question
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assessment Results Modal */}
      {showAssessmentResults && assessmentResults && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-[rgba(13,27,42,0.1)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[rgba(13,27,42,0.1)]">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border-2 border-emerald-500/50 mb-4">
                  <CheckCircle className="h-10 w-10 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-[#0D1B2A] mb-2">Assessment Complete!</h2>
                <p className="text-[#3A4A5C]">Here's your governance score breakdown</p>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Overall Score */}
              <div className="text-center p-8 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-[rgba(13,27,42,0.1)]">
                <div className="text-6xl font-bold mb-2">
                  <span className={assessmentResults.gradeColor}>{assessmentResults.grade}</span>
                </div>
                <div className="text-4xl font-bold text-[#0D1B2A] mb-1">{assessmentResults.overallScore}/100</div>
                <p className="text-[#3A4A5C]">Overall Governance Score</p>
                <Progress value={assessmentResults.overallScore} className="h-3 mt-4 max-w-xs mx-auto" />
              </div>

              {/* Category Breakdown */}
              <div>
                <h4 className="text-lg font-semibold text-[#0D1B2A] mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-400" />
                  Category Breakdown
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {GOVERNANCE_PILLARS.map(pillar => {
                    const score = assessmentResults.categoryScores[pillar.id];
                    return (
                      <div key={pillar.id} className="p-4 bg-white/5 rounded-xl border border-[rgba(13,27,42,0.1)]">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${pillar.color}20` }}>
                            <pillar.icon className="h-4 w-4" style={{ color: pillar.color }} />
                          </div>
                          <span className="text-[#0D1B2A] font-medium">{pillar.name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <Progress value={score} className="flex-1 h-2 mr-3" />
                          <span className={`text-xl font-bold ${getScoreColor(score)}`}>{score}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h4 className="text-lg font-semibold text-[#0D1B2A] mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-400" />
                  Personalized Recommendations
                </h4>
                <div className="space-y-3">
                  {assessmentResults.recommendations.map((rec, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${
                      rec.priority === "high" ? "bg-red-500/10 border-red-500/30" :
                      rec.priority === "medium" ? "bg-[#C8A84B]/10 border-yellow-500/30" : 
                      "bg-[#C8A84B]/12/10 border-blue-500/30"
                    }`}>
                      <div className="flex items-start gap-3">
                        <Badge className={`${
                          rec.priority === "high" ? "bg-red-500/20 text-red-400" :
                          rec.priority === "medium" ? "bg-[#C8A84B]/20 text-yellow-400" : 
                          "bg-[#C8A84B]/12/20 text-blue-400"
                        }`}>
                          {rec.category}
                        </Badge>
                        <p className="text-[#3A4A5C] flex-1">{rec.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Steps */}
              <div className="p-4 bg-[#C8A84B]/80/10 border border-emerald-500/30 rounded-xl">
                <h4 className="text-[#0D1B2A] font-semibold mb-2 flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-emerald-400" />
                  Next Steps
                </h4>
                <ul className="text-[#3A4A5C] space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    Review detailed recommendations in your dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    Schedule a governance review with your team
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    Track progress and retake assessment in 30 days
                  </li>
                </ul>
              </div>

              <p className="text-center text-[#7A8190] text-sm">
                Completed on {assessmentResults.completedAt}
              </p>
            </div>

            <div className="p-6 border-t border-[rgba(13,27,42,0.1)] flex justify-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAssessmentResults(false);
                  setShowAssessmentModal(false);
                  setAssessmentStep(0);
                  setAssessmentAnswers({});
                }} 
                className="border-white/20 text-[#0D1B2A]"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  setShowAssessmentResults(false);
                  setShowAssessmentModal(false);
                  setAssessmentStep(0);
                  setAssessmentAnswers({});
                  toast({
                    title: "Results Saved!",
                    description: "Your assessment has been saved to your dashboard.",
                  });
                }} 
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Save Results
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GovernanceDashboard;
